import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import './ProfileBannerHeader.css';

function ProfileBannerHeader({
  banner,
  profilePic,
  name,
  isOwnProfile = false,
  onManageBanner,
  onEditProfile,
  onViewAvatar,
  editIcon,
  isSpecialAvatar = false,
}) {
  const bannerImageRef = useRef(null);

  const [bannerLoaded, setBannerLoaded] = useState(false);
  const [bannerFailed, setBannerFailed] = useState(false);

  const bannerImageUrl = banner?.imageUrl || '';

  /*
   * Reset loading state whenever the equipped banner changes.
   */
  useEffect(() => {
    setBannerLoaded(false);
    setBannerFailed(false);
  }, [bannerImageUrl]);

  /*
   * Cached images may already be complete before React
   * receives the normal onLoad event.
   */
  useEffect(() => {
    const image = bannerImageRef.current;

    if (
      image &&
      image.complete &&
      image.naturalWidth > 0
    ) {
      setBannerLoaded(true);
    }
  }, [bannerImageUrl]);

  if (!bannerImageUrl) {
    return null;
  }

  const showBannerSkeleton =
    isOwnProfile &&
    !bannerLoaded &&
    !bannerFailed;

  const handleBannerLoad = () => {
    setBannerLoaded(true);
    setBannerFailed(false);
  };

  const handleBannerError = () => {
    setBannerLoaded(false);
    setBannerFailed(true);

    console.warn(
      '⚠️ Failed to load profile banner:',
      bannerImageUrl
    );
  };

  const avatarImage = (
    <img
      src={profilePic}
      alt={`${name || 'User'} avatar`}
      className={`profile-banner-avatar ${
        isSpecialAvatar
          ? 'profile-banner-avatar-special'
          : ''
      }`}
    />
  );

  return (
    <div className="profile-banner-header">
      <div
        className={`profile-banner-artwork ${
          bannerFailed
            ? 'profile-banner-artwork-error'
            : ''
        }`}
      >
        {showBannerSkeleton && (
          <div
            className="profile-banner-skeleton"
            aria-hidden="true"
          >
            <div className="profile-banner-skeleton-shimmer" />
          </div>
        )}

        <img
          ref={bannerImageRef}
          src={bannerImageUrl}
          alt={`${banner.name || name || 'Profile'} banner`}
          className={`profile-banner-image ${
            bannerLoaded
              ? 'profile-banner-image-loaded'
              : ''
          } ${
            bannerFailed
              ? 'profile-banner-image-failed'
              : ''
          }`}
          onLoad={handleBannerLoad}
          onError={handleBannerError}
        />

        {bannerFailed && isOwnProfile && (
          <div
            className="profile-banner-load-fallback"
            role="status"
          >
            Banner unavailable
          </div>
        )}

        {isOwnProfile &&
          typeof onManageBanner === 'function' && (
            <button
              type="button"
              className="profile-banner-manage-btn"
              onClick={onManageBanner}
              aria-label="Manage profile banner"
            >
              Manage banner
            </button>
          )}
      </div>

      <div className="profile-banner-avatar-wrap">
        {typeof onViewAvatar === 'function' ? (
          <button
            type="button"
            className={`profile-banner-avatar-view-btn ${
              isSpecialAvatar
                ? 'profile-banner-avatar-view-btn-special'
                : ''
            }`}
            onClick={onViewAvatar}
            aria-label={`View ${name || 'user'} profile picture`}
            aria-haspopup="dialog"
          >
            {avatarImage}
          </button>
        ) : (
          avatarImage
        )}

        {isOwnProfile &&
          typeof onEditProfile === 'function' &&
          editIcon && (
            <button
              type="button"
              className="profile-banner-avatar-edit-btn"
              onClick={onEditProfile}
              aria-label="Edit profile"
            >
              <img
                src={editIcon}
                alt=""
                className="profile-banner-avatar-edit-icon"
              />
            </button>
          )}
      </div>
    </div>
  );
}

export default ProfileBannerHeader;
